import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useInRouterContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../design-system/primitives/Button/Button.js';
import { useConsent } from '../../lib/cookies/useConsent.js';
import { legalPathForLanguage } from '../../lib/legalPaths.js';

type LegalLinkProps = {
  to: string;
  className: string;
  children: string;
};

function LegalLink({ to, className, children }: LegalLinkProps) {
  const inRouterContext = useInRouterContext();

  if (inRouterContext) {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <a href={to} className={className}>
      {children}
    </a>
  );
}

export function CookieBanner() {
  const { t, i18n } = useTranslation();
  const { showBanner, accept, reject } = useConsent();
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showBanner) return;
    const acceptButton = wrapperRef.current?.querySelector<HTMLButtonElement>('button');
    acceptButton?.focus();
  }, [showBanner]);

  useEffect(() => {
    if (!showBanner) return;
    const node = wrapperRef.current;
    if (!node) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        reject();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = node.querySelectorAll<HTMLElement>('a, button, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showBanner, reject]);

  if (!showBanner || typeof document === 'undefined') return null;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[65] flex justify-center border-t border-ig-border bg-ig-bg-secondary/95 p-4 backdrop-blur-sm md:p-6">
      <div
        ref={wrapperRef}
        aria-live="polite"
        className="pointer-events-auto w-full max-w-[1200px] translate-y-0 animate-[fadeInUp_220ms_ease-out] rounded-lg border border-ig-border bg-ig-bg-secondary p-5 shadow-lg md:p-6"
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-8">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-ig-accent">{t('legal.banner.label')}</p>
            <p className="mt-2 max-w-[760px] font-sans text-[14px] leading-6 text-ig-text-secondary">
              {t('legal.banner.bodyPrefix')}{' '}
              <LegalLink
                to={legalPathForLanguage(i18n.language, '/cookies')}
                className="text-ig-accent underline underline-offset-2"
              >
                {t('landing.footer.legalLinks.cookies')}
              </LegalLink>
              . {t('legal.banner.bodySuffix')}{' '}
              <LegalLink
                to={legalPathForLanguage(i18n.language, '/privacy')}
                className="text-ig-accent underline underline-offset-2"
              >
                {t('landing.footer.legalLinks.privacy')}
              </LegalLink>
              .
            </p>
          </div>

          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={accept}
              className="h-11 w-full rounded-lg md:w-auto md:rounded-md"
            >
              {t('legal.banner.accept')}
            </Button>
            <Button type="button" variant="secondary" size="lg" onClick={reject} className="h-11 w-full md:w-auto">
              {t('legal.banner.reject')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
